import { useEffect, useState } from 'react'
import Label from '../../ui/label';
import { Spinner } from '../../../assets/icon/svg';
import ButtonDropdownOption from '../../ui/button-dropdown-option';
import Input from '../../ui/input';
import { selectCreateAccountState, updateField } from '../../../features/it-user-forms/user-forms-slice';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '../../../hooks/services/mutations/use-mutation';
import { useCreateUser } from '../../../hooks/actions/use-create-user';
import { usePopulateProfileForm } from '../../../hooks/populate-data/use-populate-profile-from';
import { UserInfo } from '../../../api/user/user-api-state';
import { customStyle, defaultStyle } from '../../../constant/custom-style';
import ConfirmDefaultModal from '../../global/modal/confirm-default-modal';

interface Props {
    userData: UserInfo | undefined;
}

export default function UpdateProfile({ userData }: Props) {

    const dispatch = useDispatch()
    const { defaultOptionSuffix, searchTypeSuffix, setSearchTypeSuffix, suffixOptions, handleSaveUpdateMyAccount, handleCancelUpdateMyAccount, } = useCreateUser()
    const { isLoadingUpdateProfile, } = useCreateUser()
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const profile = useSelector(selectCreateAccountState)
    
    const { populateProfileUpdateForm } = usePopulateProfileForm({
        suffixOptions,
        defaultOptionSuffix,
        setSearchTypeSuffix,
    });

    useEffect(() => {
        if(!userData) return 
        populateProfileUpdateForm(userData)
    }, [userData, populateProfileUpdateForm]);

    useEffect(() => {
        if (!userData) return;
        const selectedSuffix = suffixOptions.find(item => item.name === userData.suffix) || defaultOptionSuffix;
        const changed =
            profile.firstname !== (userData.firstname ?? "") ||
            profile.middlename !== (userData.middlename ?? "") ||
            profile.lastname !== (userData.lastname ?? "") ||
            profile?.suffix !== (selectedSuffix ?? "") ||
            profile.phoneNumber !== (userData.phoneNumber ?? "");

        setHasChanges(changed);
    }, [profile, userData]);

    if (!userData) return null;

    const isDisabled = isLoadingUpdateProfile || !hasChanges;

    return (
        <>
            <div className="">
                <section className="rounded-xl border border-gray-200 bg-white">
                    {/* HEADER */}
                    <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
                        <div>
                            <p className="text-xs font-semibold text-gray-800">
                                Update Profile
                            </p>
                            <p className="mt-0.5 text-[10px] text-gray-500">
                                Update your personal information and contact details.
                            </p>
                        </div>
                    </div>
                    <div className='p-3 space-y-4'>
                    {/* PERSONAL INFORMATION */}
                    <div className="bg-white rounded-xl border">
                        <div className=" bg-gray-50 rounded-t-xl px-3 py-2">
                            <p className="text-xs font-semibold text-gray-700">
                                Personal Information
                            </p>
                            <p className="mt-0.5 text-[10px] text-gray-400">
                                Make sure your name matches your official records.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 gap-4 p-3 sm:grid-cols-2 lg:grid-cols-3">
                            {/* FIRST NAME */}
                            <div>
                                <Label
                                    label="First Name"
                                    fontSize="text-xs mb-1"
                                    requiredText=" *"
                                    isRequired={true}
                                />
                                <Input
                                    type="text"
                                    name="firstname"
                                    value={profile.firstname}
                                    isCustomStyle={true}
                                    customCss={`${defaultStyle} ${ !profile.firstname ? '' : customStyle }`}
                                    onChange={(e) => dispatch(updateField({ field: 'firstname', value: e.target.value }))}
                                    required={true}
                                    placeholder="Enter first name"
                                    disabled={isLoadingUpdateProfile}
                                />
                            </div>

                            {/* MIDDLE NAME */}
                            <div>
                                <Label
                                    label="Middle Name"
                                    fontSize="text-xs mb-1"
                                />
                                <Input
                                    type="text"
                                    name="middlename"
                                    value={profile.middlename}
                                    isCustomStyle={true}
                                    customCss={`${defaultStyle} ${ !profile.middlename ? '' : customStyle }`}
                                    onChange={(e) => dispatch(updateField({ field: 'middlename', value: e.target.value }))}
                                    required={true}
                                    placeholder="Enter middle name"
                                    disabled={isLoadingUpdateProfile}
                                />
                            </div>

                            {/* LAST NAME */}
                            <div>
                                <Label
                                    label="Last Name"
                                    fontSize="text-xs mb-1"
                                    requiredText=" *"
                                    isRequired={true}
                                />
                                <Input
                                    type="text"
                                    name="lastname"
                                    value={profile.lastname}
                                    isCustomStyle={true}
                                    customCss={`${defaultStyle} ${ !profile.lastname ? '' : customStyle }`}
                                    onChange={(e) => dispatch(updateField({ field: 'lastname', value: e.target.value }))}
                                    required={true}
                                    placeholder="Enter last name"
                                    disabled={isLoadingUpdateProfile}
                                />
                            </div>

                            {/* SUFFIX */}
                            <div>
                                <Label
                                    label="Suffix"
                                    fontSize="text-xs mb-1"
                                />
                                <ButtonDropdownOption 
                                    width="w-full" 
                                    padding="px-3 py-2" 
                                    options={suffixOptions} 
                                    isLoading={false} 
                                    value={searchTypeSuffix} 
                                    onChange={(option) => {
                                        setSearchTypeSuffix(option);
                                        dispatch(updateField({ field: 'suffix', value: option }));
                                    }}
                                    open={openDropdown === 'suffix'} 
                                    setOpen={() => setOpenDropdown(openDropdown === 'suffix' ? null : 'suffix')} 
                                />
                            </div>

                            {/* PHONE NUMBER */}
                            <div className="sm:col-span-2 lg:col-span-1">
                                <Label
                                    label="Mobile Number"
                                    fontSize="text-xs mb-1"
                                    requiredText=" *"
                                    isRequired={true}
                                />
                                <div
                                    className={`flex rounded border overflow-hidden focus-within:border-[#186cb2] border-gray-300 ${
                                        profile.phoneNumber.trim() !== ''
                                            ? ''
                                            : ''
                                    }`}
                                >
                                    <ButtonDropdownOption
                                        isShowArrow={false}
                                        isDisable={false}
                                        width="w-auto"
                                        padding="px-2 py-[6px] rounded-l rounded-r-none border-none"
                                        options={[]}
                                        isLoading={false}
                                        value={{id: 1, name: '+63'}}
                                        onChange={(option) => {}}
                                        open={false}
                                        isNoBorder={true}
                                        setOpen={() =>{}}
                                    />
                                    <Input
                                        type="text"
                                        name="phoneNumber"
                                        value={profile.phoneNumber}
                                        isCustomStyle={true}
                                        customCss={`rounded-tr rounded-br w-full text-xs px-2 py-1.5 outline-none ${profile.phoneNumber.trim() === '' ? '' : customStyle }`}
                                        onChange={(e) => {
                                            let value = e.target.value;
                                            value = value.replace(/\D/g, '');
                                            if (value.length > 10) {
                                                value = value.slice(0, 10);
                                            }
                                            if ( value === '' || value.startsWith('9') ) {
                                                dispatch(updateField({ field: 'phoneNumber', value, }), );
                                            }
                                        }}
                                        placeholder="9123456789"
                                        required={true}
                                        disabled={isLoadingUpdateProfile}
                                        maxLength={10}
                                    />
                                </div>
                                <p className="mt-1 text-[10px] text-gray-400">
                                    Enter your 10-digit mobile number without +63.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* ACCOUNT INFORMATION */}
                    <div className="bg-white rounded-xl border">
                        <div className=" bg-gray-50 rounded-t-xl px-3 py-2">
                            <p className="text-xs font-semibold text-gray-700">
                                Account Information
                            </p>
                            <p className="mt-0.5 text-[10px] text-gray-400">
                                The following information is managed by the system.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 lg:grid-cols-3">
                            <div>
                                <Label
                                    label="Email Address"
                                    fontSize="text-xs mb-1"
                                />
                                <Input
                                    type="text"
                                    name="email"
                                    value={userData.email ?? ''}
                                    isCustomStyle={true}
                                    customCss={`cursor-not-allowed bg-gray-100 text-gray-500 ${defaultStyle} ${userData.email ? customStyle : ''}`}
                                    onChange={() => {}}
                                    required={true}
                                    disabled={true}
                                />
                            </div>

                            <div>
                                <Label
                                    label="Department"
                                    fontSize="text-xs mb-1"
                                />
                                <Input
                                    type="text"
                                    name="department"
                                    value={userData.department?.name ?? ''}
                                    isCustomStyle={true}
                                    customCss={`cursor-not-allowed bg-gray-100 text-gray-500 ${defaultStyle} ${userData.department?.name ? customStyle : ''}`}
                                    onChange={() => {}}
                                    required={true}
                                    disabled={true}
                                />
                            </div>

                            <div>
                                <Label
                                    label="Position"
                                    fontSize="text-xs mb-1"
                                />
                                <Input
                                    type="text"
                                    name="position"
                                    value={userData.position ?? ''}
                                    isCustomStyle={true}
                                    customCss={`cursor-not-allowed bg-gray-100 text-gray-500 ${defaultStyle} ${userData.position ? customStyle : ''}`}
                                    onChange={() => {}}
                                    required={true}
                                    disabled={true}
                                />
                            </div>
                        </div>
                    </div>
                    </div>
                </section>
            </div>

            {/* ACTIONS */}
            <div className="flex flex-col-reverse gap-2 pt-4 sm:flex-row sm:items-center sm:justify-end">
                <button
                    type="button"
                    disabled={isLoadingUpdateProfile}
                    onClick={handleCancelUpdateMyAccount}
                    className="flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-gray-600 transition hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        className="h-3.5 w-3.5"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 6l12 12M18 6 6 18"
                        />
                    </svg>
                    <span>Cancel</span>
                </button>

                <button
                    type="button"
                    disabled={isDisabled}
                    onClick={() => setShowConfirmModal(true)}
                    className={`flex items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold text-white shadow-sm transition active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-[#186cb2]/20 disabled:cursor-not-allowed disabled:opacity-50 ${isDisabled ? "bg-gray-400" : "bg-[#186cb2] hover:bg-[#155d9b]"}`}
                >
                    {isLoadingUpdateProfile ? (
                        <>
                            <Spinner size={13} />
                            <span>Updating...</span>
                        </>
                    ) : (
                        <>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                className="h-3.5 w-3.5"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M5 12h14"
                                />
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="m13 6 6 6-6 6"
                                />
                            </svg>
                            <span>Update Profile</span>
                        </>
                    )}
                </button>
            </div>
            {showConfirmModal && 
                <ConfirmDefaultModal
                    modalTitle="Confirm Profile Update"
                    modalSubTitle="Are you sure you want to update your profile? Your current personal information will be replaced with the changes you made."
                    onCancel={() => setShowConfirmModal(false)}
                    onConfirm={handleSaveUpdateMyAccount}
                    isLoading={isLoadingUpdateProfile}
                />
            }
        </>
    )
}